import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { t } from "@/i18n/pl";
import { invalidateGroups, queryClient, queryKeys } from "@/lib/query-client";
import { ApiError } from "@/services/api-client";
import * as groupsService from "@/services/groups.service";
import type {
  DayStatusSetInput,
  GroupCreateInput,
  GroupUpdateInput,
  MembershipStatus,
} from "@/types/api";

function toastError(error: unknown): void {
  toast.error(error instanceof ApiError ? error.message : t.common.genericError);
}

export function useMyGroups() {
  return useQuery({
    queryKey: queryKeys.myGroup,
    queryFn: groupsService.getMyGroups,
    // The home screen's heartbeat: picks up day-status changes and accepted
    // requests without a manual reload.
    refetchInterval: 30_000,
  });
}

export function useGroups() {
  return useQuery({
    queryKey: queryKeys.groups,
    queryFn: groupsService.listGroups,
    refetchInterval: 60_000,
  });
}

export function useGroup(groupId: string) {
  return useQuery({
    queryKey: queryKeys.group(groupId),
    queryFn: () => groupsService.getGroup(groupId),
  });
}

export function useJoinRequests(groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.joinRequests(groupId),
    queryFn: () => groupsService.listJoinRequests(groupId),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useMessages(groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.messages(groupId),
    queryFn: () => groupsService.listMessages(groupId),
    enabled,
    // Chat "live enough" without WebSockets: a 5s poll is invisible at crew
    // scale (≤9 people) and keeps the whole stack boring and debuggable.
    refetchInterval: 5_000,
  });
}

export function useCreateGroup() {
  return useMutation({
    mutationFn: (input: GroupCreateInput) => groupsService.createGroup(input),
    onSuccess: () => {
      invalidateGroups();
      toast.success(t.createGroup.created);
    },
    onError: toastError,
  });
}

export function useUpdateGroup(groupId: string) {
  return useMutation({
    mutationFn: (input: GroupUpdateInput) => groupsService.updateGroup(groupId, input),
    onSuccess: invalidateGroups,
    onError: toastError,
  });
}

/** Ends a car for good (a hard delete): from its settings page or from the
 *  admin "Grupy" tab, each with its own toast. */
export function useDeleteGroup(successMessage: string) {
  return useMutation({
    mutationFn: (groupId: string) => groupsService.deleteGroup(groupId),
    onSuccess: () => {
      invalidateGroups();
      // The admin CO₂ estimate counts cars.
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminStats });
      toast.success(successMessage);
    },
    onError: toastError,
  });
}

export function useRequestJoin(groupId: string) {
  return useMutation({
    mutationFn: ({ message, days }: { message: string | null; days: number[] | null }) =>
      groupsService.requestJoin(groupId, message, days),
    onSuccess: () => {
      invalidateGroups();
      toast.success(t.group.joinSent);
    },
    onError: toastError,
  });
}

export function useDecideMembership() {
  return useMutation({
    mutationFn: ({
      membershipId,
      action,
    }: {
      membershipId: string;
      action: Extract<MembershipStatus, "active" | "declined" | "removed">;
    }) => groupsService.decideMembership(membershipId, action),
    onSuccess: (_, { action }) => {
      invalidateGroups();
      if (action === "active") toast.success(t.driver.accepted);
      else if (action === "declined") toast.success(t.driver.declined);
      else toast.success(t.driver.removed);
    },
    onError: toastError,
  });
}

export function useLeaveGroup() {
  return useMutation({
    mutationFn: ({ membershipId }: { membershipId: string; wasPending: boolean }) =>
      groupsService.leaveGroup(membershipId),
    onSuccess: (_, { wasPending }) => {
      invalidateGroups();
      toast.success(wasPending ? t.group.requestWithdrawn : t.group.left);
    },
    onError: toastError,
  });
}

export function usePostMessage(groupId: string) {
  return useMutation({
    mutationFn: (body: string) => groupsService.postMessage(groupId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(groupId) });
    },
    onError: toastError,
  });
}

export function useAddAbsence() {
  return useMutation({
    mutationFn: ({ membershipId, date }: { membershipId: string; date: string }) =>
      groupsService.addAbsence(membershipId, date),
    onSuccess: () => {
      invalidateGroups();
      toast.success(t.absence.added);
    },
    onError: toastError,
  });
}

export function useRemoveAbsence() {
  return useMutation({
    mutationFn: ({ membershipId, absenceId }: { membershipId: string; absenceId: string }) =>
      groupsService.removeAbsence(membershipId, absenceId),
    onSuccess: () => {
      invalidateGroups();
      toast.success(t.absence.removed);
    },
    onError: toastError,
  });
}

export function useSetDayStatus(groupId: string) {
  return useMutation({
    mutationFn: (input: DayStatusSetInput) => groupsService.setDayStatus(groupId, input),
    onSuccess: () => {
      invalidateGroups();
      toast.success(t.status.updated);
    },
    onError: toastError,
  });
}

export function useUpdateMembershipDays() {
  return useMutation({
    mutationFn: ({ membershipId, days }: { membershipId: string; days: number[] }) =>
      groupsService.updateMembershipDays(membershipId, days),
    onSuccess: () => {
      invalidateGroups();
      toast.success(t.group.daysUpdated);
    },
    onError: toastError,
  });
}
